import { Link } from "react-router-dom";
import { useEffect } from "react";
import JsonLd from "@/components/base/JsonLd";
import { articlesMeta, buildArticleJsonLd } from "@/mocks/seoMetadata";

const article = articlesMeta.find((a) => a.slug === "/articoli/sicurezza-itaobuy-spreadsheet")!;

export default function SicurezzaPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleJsonLd(article)} />
      <header className="w-full px-6 md:px-10 lg:px-16 py-5 bg-background-50 border-b border-background-200/70 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-2xl font-bold text-accent-500 tracking-tight">ItaoBuy</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-1.5 px-4 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-accent-600">
              itaobuy sheet
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-primary-600">
              buy it now
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs"></i></span>
            </a>
          </div>
        </div>
      </header>

      <article className="w-full px-6 md:px-10 lg:px-16 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
            <span className="text-foreground-400 whitespace-nowrap">Sicurezza ItaoBuy Spreadsheet</span>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">Security Guide</span>
              <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">Privacy</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              Sicurezza e Privacy nel Tuo ItaoBuy Spreadsheet
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
              Proteggi i tuoi dati sensibili con le migliori pratiche di sicurezza per <strong>ItaoBuy Spreadsheet</strong>. Crittografia, permessi di accesso, autenticazione a due fattori e strategie di backup per la massima tranquillità.
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span>Aggiornato: Luglio 2026</span>
              <span>|</span>
              <span>Tempo di lettura: 8 minuti</span>
            </div>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img src="https://readdy.ai/api/search-image?query=Modern%20cybersecurity%20workspace%20with%20laptop%20displaying%20data%20protection%20dashboard%2C%20shield%20icons%20and%20lock%20symbols%20on%20screen%2C%20warm%20cream%20desk%20setup%2C%20soft%20natural%20lighting%2C%20professional%20data%20security%20photography%2C%20clean%20professional%20composition&width=1200&height=600&seq=article-security-hero&orientation=landscape" alt="Sicurezza e Privacy ItaoBuy Spreadsheet" title="Sicurezza ItaoBuy Spreadsheet - Protezione Dati e Privacy" className="w-full h-auto object-cover" />
          </div>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Perché la Sicurezza di ItaoBuy Spreadsheet è Fondamentale</h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
              Il tuo <strong>ItaoBuy Spreadsheet</strong> contiene dati preziosi: liste di prodotti, prezzi di acquisto, fornitori, tracking degli ordini e informazioni finanziarie. Se questi dati cadono in mani sbagliate, il danno può essere significativo. Per questo motivo, adottare pratiche di sicurezza solide non è opzionale.
            </p>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
              Google Sheets offre già un livello base di sicurezza grazie all&apos;infrastruttura Google, ma ci sono passaggi aggiuntivi che puoi intraprendere per rendere il tuo <strong>ItaoBuy Spreadsheet</strong> veramente a prova di intrusione.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">Migliori Pratiche di Sicurezza</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">1</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Gestione dei Permessi di Accesso</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Non condividere mai il tuo <strong>ItaoBuy Spreadsheet</strong> con &ldquo;chiunque abbia il link&rdquo;. Utilizza sempre la condivisione tramite email specifica con livelli di permesso appropriati: visualizzazione per i collaboratori che non devono modificare, commento per chi deve suggerire cambiamenti, e modifica solo per chi gestisce attivamente i dati.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">2</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Autenticazione a Due Fattori (2FA)</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Abilita l&apos;autenticazione a due fattori sul tuo account Google. Anche se qualcuno dovesse ottenere la tua password, non potrà accedere al tuo <strong>ItaoBuy Spreadsheet</strong> senza il secondo fattore di verifica, tipicamente un codice sul tuo smartphone.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">3</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Strategie di Backup Regolari</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Configura backup automatici tramite Google Apps Script o strumenti di terze parti. Esporta periodicamente in formato Excel (.xlsx) e archivia le copie in una cartella protetta su Google Drive. La regola d&apos;oro: tre copie in due luoghi diversi.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-accent-500 text-background-50 rounded-full font-bold text-sm">4</div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-1">Crittografia e Dati Sensibili</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    Evita di memorizzare password, dati bancari completi o informazioni di pagamento direttamente nel <strong>ItaoBuy Spreadsheet</strong>. Se devi includere dati sensibili, utilizza celle protette con password o considera l&apos;uso di strumenti di crittografia aggiuntivi.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-10 p-6 md:p-8 bg-accent-500 rounded-2xl text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">Proteggi il Tuo ItaoBuy Spreadsheet Oggi</h2>
            <p className="text-white/80 text-sm md:text-base mb-6 max-w-lg mx-auto">
              Inizia subito a utilizzare <strong className="text-white">ItaoBuy Spreadsheet</strong> con la sicurezza come priorità assoluta.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 px-6 py-3 bg-white text-accent-500 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-white/90">
                Visita itaobuy sheet <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
              </a>
              <Link to="/" className="flex items-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm border border-white/30 text-white rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all duration-200 hover:bg-white/25">
                Torna alla Home <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              </Link>
            </div>
          </section>

          <nav className="flex items-center justify-between pt-6 border-t border-background-200/70">
            <Link to="/" className="flex items-center gap-2 text-sm text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span> ItaoBuy Spreadsheet Home
            </Link>
            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-400">Condividi:</span>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="Condividi su X"><i className="ri-twitter-x-line text-sm"></i></a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-200 cursor-pointer" aria-label="Condividi su Facebook"><i className="ri-facebook-fill text-sm"></i></a>
            </div>
          </nav>
        </div>
      </article>

      <footer className="w-full px-4 md:px-8 pt-4 pb-6 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-foreground-500">&copy; {new Date().getFullYear()} ItaoBuy Spreadsheet Italia. <Link to="/" className="text-accent-500 hover:text-accent-600 transition-colors cursor-pointer ml-1">Torna alla Home</Link></p>
        </div>
      </footer>
    </main>
  );
}